<template>
  <InputGroup
    v-bind="inputAttrs"
  >
    <template
      v-if="$slots.left
        || $slots.addonBefore
        || $slots.prefix"
      #left
    >
      <!-- @slot 左侧 widget -->
      <slot name="left" />
      <NewBsBox
        v-if="$slots.addonBefore"
        class="addonBefore"
        :bs="{ 'cmp.inputGroup': 'addonBefore' }"
      >
        <!-- @slot 左侧灰色方格 -->
        <slot name="addonBefore" />
      </NewBsBox>
      <NewBsBox
        v-if="$slots.prefix"
        class="prefix"
        :bs="{ 'cmp.inputGroup': 'affix', pr: 0 }"
      >
        <!-- @slot 前缀文字 -->
        <slot name="prefix" />
      </NewBsBox>
    </template>

    <template
      v-if="showRightSlot
        || $slots.addonAfter
        || $slots.suffix"
      #right
    >
      <BeerIcon
        v-if="showClear"
        :size="14"
        :bs="{
          mr: 12, transform: 'rotate(45deg)',
          fill: 'ash',
          '&:hover': { fill: 'mute' }
        }"
        aria-label="clear"
        icon="add-f"
        @click.stop="handleClear"
      />
      <NewBsBox
        v-else-if="maxLen"
        :bs="{
          mr: 12, color: exceedsLength ? 'danger' : 'ash',
          'cmp.inputGroup': 'length',
        }"
      >
        {{ lengthText }}
      </NewBsBox>
      <NewBsBox
        v-if="$slots.addonAfter"
        class="addonAfter"
        :bs="{ 'cmp.inputGroup': 'addonAfter' }"
      >
        <!-- @slot 右侧灰色方格 -->
        <slot name="addonAfter" />
      </NewBsBox>
      <NewBsBox
        v-if="$slots.suffix"
        class="suffix"
        :bs="{ 'cmp.inputGroup': 'affix', pl: 0 }"
      >
        <!-- @slot 后缀文字 -->
        <slot name="suffix" />
      </NewBsBox>
    </template>
  </InputGroup>
</template>

<script lang="ts">
  import {
    defineComponent, PropType, computed, ref,
  } from 'vue'
  import { isNil } from 'lodash'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import InputGroup from '@xhs/yam-beer/cmp/dataEntry/InputGroup/InputGroup.vue'
  import { BeerStyleObj } from '../../../core'

  /**
   * 基本表单文字输入控件
   * @see 继承InputGroup #InputGroup
   */
  export default defineComponent({
    name: 'BeerInput',
    components: {
      NewBsBox,
      InputGroup,
      BeerIcon,
    },
    inheritAttrs: false,
    props: {
      /**
       * 控制的值
       */
      modelValue: {
        type: [String, Number],
        required: false,
      },
      /**
       * @ignore
       *  容器 Bs，传递给 InputGroup
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * @ignore
       * 是否只读显示
       */
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否显示清除文字按钮
       */
      clearable: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否禁止
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * @ignore
       * placeholder 的快捷 props
       */
      placeholder: {
        type: String,
        default: '请输入',
      },
      /**
        * @ignore
       */
      type: {
        type: String,
        default: 'text',
      },
      /**
       * 字数限制
       */
      maxlength: {
        type: Number,
        default: undefined,
      },
    },
    emits: {
      'update:modelValue': null,
      /**
       * 点击清除按钮的操作
       */
      clearValue: null,
    },
    setup(props, { emit, attrs }) {
      const inputRef = ref()
      const strValue = computed(() => {
        if (isNil(props.modelValue)) {
          return ''
        }
        return String(props.modelValue)
      })
      const maxLen = computed(() => {
        if (props.type === 'number') return null
        if (props.disabled) return null
        return props.maxlength
      })
      const currentLen = computed(() => strValue.value?.length || 0)
      const lengthText = computed(() => `${currentLen.value}/${maxLen.value}`)
      const showClear = computed(() => {
        if (!props.clearable) {
          return false
        }
        if (isNil(props.modelValue)) {
          return false
        }
        return props.modelValue !== ''
      })
      const showRightSlot = computed(() => {
        if (props.readonly) {
          return false
        }
        return showClear.value || maxLen.value
      })
      const exceedsLength = computed(() => {
        if (!maxLen.value) {
          return false
        }
        return currentLen.value > maxLen.value
      })
      const handleInput = (e: KeyboardEvent) => {
        emit('update:modelValue', (e.target as HTMLInputElement).value)
      }
      const handleClear = () => {
        emit('update:modelValue', '')
        emit('clearValue')
      }

      const inputAttrs = computed(() => {
        const attr: any = {
          bs: props.bs,
          readonly: props.readonly,
          placeholder: props.placeholder || null,
          type: props.type,
          maxlength: maxLen.value,
          onInput: handleInput,
          ref: (cmp: any) => {
            inputRef.value = cmp
          },
          ...attrs,
        }
        if (props.disabled) {
          attr.disabled = true
        }
        if (exceedsLength.value) {
          attr['data-error'] = true
        }
        if (!isNil(props.modelValue)) {
          attr.value = props.modelValue
        }
        return attr
      })

      return {
        focus(opt: any) {
          inputRef.value.focus(opt)
        },
        blur() {
          inputRef.value.blur()
        },
        inputAttrs,
        lengthText,
        maxLen,
        showClear,
        showRightSlot,
        exceedsLength,
        handleClear,
      }
    },
  })
</script>
